import type { ClientSettings } from './types';

/**
 * Default client settings keyed by locale. Use `getDefaultClientSettings(locale)`
 * rather than hardcoding market/country/language/currency across the codebase.
 *
 * In production, prefer the SSM-injected values (via the origin lambda's
 * locale-config script tag or SearchClientSettings from parameter store).
 * These defaults serve as the single source of truth for:
 *   - Server-side enhancers during static generation
 *   - Client-side fallback when the injected config is absent (local dev)
 *   - Test fixtures
 *
 * The map and its entries are frozen to prevent accidental mutation.
 */
const CLIENT_SETTINGS_BY_LOCALE: Readonly<Record<string, Readonly<ClientSettings>>> = Object.freeze({
  'en-GB': Object.freeze({ market: 'UK_V8', country: 'UK', language: 'EN', currency: 'GBP' } as const),
  'de-DE': Object.freeze({ market: 'DE', country: 'DE', language: 'DE', currency: 'EUR' } as const),
});

const DEFAULT_LOCALE = 'en-GB';

/**
 * Normalises a locale string to the canonical form used in
 * CLIENT_SETTINGS_BY_LOCALE (e.g. "en-GB", "de-DE").
 * Handles lowercase ("de-de"), uppercase ("DE-DE"), and mixed inputs.
 */
const normalizeLocale = (locale: string): string => {
  const parts = locale.split('-');
  if (parts.length === 2) {
    return `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
  }
  return locale;
};

/**
 * Returns the default {@link ClientSettings} for a given locale.
 * The lookup is case-insensitive (e.g. "de-de", "DE-DE", "de-DE" all work).
 * Falls back to en-GB if the locale is not recognised.
 *
 * Returns a shallow copy so callers cannot mutate the internal defaults.
 */
export const getDefaultClientSettings = (
  locale: string = DEFAULT_LOCALE
): ClientSettings => {
  const normalized = normalizeLocale(locale);
  const settings =
    CLIENT_SETTINGS_BY_LOCALE[normalized] ??
    CLIENT_SETTINGS_BY_LOCALE[DEFAULT_LOCALE];
  return { ...settings };
};
